// Single source of truth for these types lives in types/index.ts.
// Re-export so callers can import everything from this module.
export type { FundraisingWindows, FundraisingWindowPair } from '../types';

import type {
  FundraisingWindows, CockpitRepRow, CockpitSeason, CockpitFundraisingWindowBounds,
} from '../types';

// 'calYtd' — calendar year to date (payment date).
// 'season' — the team's Blast school year (CRM-120). The ONLY thing called a
// season in this app; the old payment-date SY metric was removed once the rep
// Today card moved over, so a new screen cannot wire itself to the wrong one.
export type FundMetric = 'calYtd' | 'season';
export type FundInterp = 'received' | 'launched';

export interface PickedWindow {
  raisedCents: string;
  priorCents: string;
  yoyPct: number | null;
  campaigns: number;
  label: string;
  priorLabel: string;
}

/** Two-decimal YoY percent; null when prior is zero (caller renders "New"). */
export function windowYoyPct(thisCents: string, priorCents: string): number | null {
  const prior = BigInt(priorCents);
  if (prior === 0n) return null;
  return Number(((BigInt(thisCents) - prior) * 10000n) / prior) / 100;
}

/** "2026-27" from the END-year season number 2027. */
export function seasonLabel(endYear: number): string {
  return `${endYear - 1}-${String(endYear % 100).padStart(2, '0')}`;
}

/** "Jul 29, 2026" — UTC, because the bounds are UTC and a local render would
 *  show the wrong day for anyone west of Greenwich near midnight. */
function fmtDay(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
}

/** "Jan 1" — no year, for the start of a range whose end already carries one. */
function fmtDayNoYear(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', timeZone: 'UTC',
  });
}

/**
 * The one-line caption that tells a rep what window they are looking at (CRM-121).
 * Nothing on this page used to say, which is the root cause the whole investigation
 * traced back to: the numbers were defensible but unreadable.
 *
 * Built from the server's own bounds, never from a recomputed range — a second
 * implementation of this date math would drift from the money it describes.
 *
 * Returns '' when the data needed to be accurate is absent. Showing nothing beats
 * showing a window the numbers were not actually cut at.
 */
export function windowCaption(
  metric: FundMetric,
  bounds: CockpitFundraisingWindowBounds | undefined,
  season: CockpitSeason | undefined,
): string {
  if (!bounds) return '';

  if (metric === 'season') {
    if (!season) return '';
    // A season is a set of TEAMS, not a date range — so this deliberately names the
    // season and the date money was counted through, and never implies a start date.
    return `${seasonLabel(season.currentSeasonYear)} teams · money received through ${fmtDay(bounds.asOf)}`
      + ` · vs ${seasonLabel(season.priorSeasonYear)} through ${fmtDay(bounds.priorAsOf)}`;
  }

  return `${fmtDayNoYear(bounds.calYtdStart)} – ${fmtDay(bounds.calYtdEnd)}`
    + ` · vs ${fmtDayNoYear(bounds.priorCalYtdStart)} – ${fmtDay(bounds.priorCalYtdEnd)}`;
}

// DEFAULT interpretation. Flip to 'launched' here (one line) to switch the whole
// app to the "campaigns launched" definition without backend changes (Fork 1 hedge).
const DEFAULT_INTERP: FundInterp = 'received';

export function pickWindow(
  windows: FundraisingWindows,
  metric: FundMetric,
  opts: { interp?: FundInterp; season?: CockpitSeason } = {},
): PickedWindow {
  const interp = opts.interp ?? DEFAULT_INTERP;
  const { season } = opts;
  if (metric === 'season') {
    const raisedCents = season?.current ?? '0';
    const priorCents = season?.prior ?? '0';
    return {
      raisedCents,
      priorCents,
      yoyPct: windowYoyPct(raisedCents, priorCents),
      campaigns: season?.campaignsCurrent ?? 0,
      label: season ? seasonLabel(season.currentSeasonYear) : 'season',
      priorLabel: season ? seasonLabel(season.priorSeasonYear) : 'last season',
    };
  }
  // Only 'calYtd' can reach here — 'season' returns above.
  const cur = windows.calYtd;
  const prior = windows.priorCalYtd;
  const raisedCents = cur[interp];
  const priorCents = prior[interp];
  return {
    raisedCents,
    priorCents,
    yoyPct: windowYoyPct(raisedCents, priorCents),
    campaigns: windows.campaignsCalYtd,
    label: 'YTD',
    priorLabel: 'last year to date',
  };
}

/** Map a rep row's selected fundraising window into the legacy flat fields the
 *  cockpit table/panel render (so $ columns, YoY pill, sort, and CSV all follow
 *  the YTD/Season toggle together). Rows without a windows block pass through. */
export function mapRepRowWindow(row: CockpitRepRow, metric: FundMetric): CockpitRepRow {
  if (!row.windows) return row;
  const w = pickWindow(row.windows, metric, { season: row.season });
  return {
    ...row,
    raisedThisSyCents: w.raisedCents,
    raisedPriorSyCents: w.priorCents,
    campaignsThisSy: w.campaigns,
    fundraiserYoyPct: w.yoyPct,
  };
}
